import * as React from 'react';
import { Theme } from '@mui/material/styles';

import { withStyles } from 'tss-react/mui';

import { IRadBaseradAnalys } from '../Queries/EkonomiskAnalysRadBaseradQuery/EkonomiskAnalysRadBaseradQuery';
import EDTable from '../../../../../../BaseComponents/Table/EDTable';
import EDTableHead from '../../../../../../BaseComponents/Table/Components/EDTableHead';
import TableCell from './EkonomiskAnalysTableCell/EkonomiskAnalysTableCell';
import BaseTableCell from '../../../../../../BaseComponents/Table/Components/EDTableCell';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDTableBody from '../../../../../../BaseComponents/Table/Components/EDTableBody';
import EDTableRow from '../../../../../../BaseComponents/Table/Components/EDTableRow';
import EkonomiskAnalysDetailInfo from './AnalysDetailInfo/EkonomiskAnalysDetailInfo';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from './EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../EkonomiskAnalysPage';
import { IColumn } from './EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import EDTooltip from '../../../../../../BaseComponents/Tooltip/EDTooltip';
import { TECKENVAND_KOLUMN } from '../Const/EkonomiskAnalysConsts';
import DefaultRowType from './EkonomiskAnalysRadBaseradRow/Components/DefaultRowType/DefaultRowType';
import RowTypeS from './EkonomiskAnalysRadBaseradRow/Components/RowTypeS/RowTypeS';

interface IProps {
  analys: IRadBaseradAnalys;
  selectedAnalys: ISelectedAnalys;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  analysSettings: IFilledLocalAnalys;
  updatedAnalysSettings: ILocalAnalys;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  onDenominationChange: (denomination: number) => void;
  showComparisonNumber: boolean;
  handleScroll: (event: React.UIEvent) => void;
  inititialLopandeUtanSummeringRowsPerPage?: number | null;
  classes?: any;
}

interface IState {
  examineIndex?: number;
  hoverRowIndex: number;
}

const styles = (theme: Theme) => ({
  scrollContainer: {
    width: '100%',
    overflow: 'auto',
    '-webkit-overflow-scrolling': 'touch',
  },
  columnTitle: {
    fontWeight: 'bold',
    color: theme.customProperties.Fade.DarkOnLight.mediumEmphasis,
  },
  tableCell: {
    position: 'relative',
    '& .colPaint': {
      display: 'none',
      position: 'absolute',
      left: 0,
      width: '100%',
      backgroundColor: theme.customProperties.Fade.BlueGray.selectedLightFade,
      mixBlendMode: 'multiply',
      zIndex: 0,
      pointerEvents: 'none',
    },
    '&:hover .colPaint': {
      display: 'block',
    },
  },
  notCell: {
    width: 75,
  },
  table: {
    tableLayout: 'auto',
  },
  firstColumnHeadCell: {
    '&&&': {
      width: 400,
      paddingBottom: '16px',
    },
  },
  columnHead: { '&&&': { width: 50, paddingBottom: '16px' } },
  lastColumnHeadCell: {
    '&:last-child': {
      padding: 0,
      top: 0,
      zIndex: 120,
      position: 'sticky',
      verticalAlign: 'bottom',
      backgroundColor: theme.palette.common.white,
      width: '100%',
    },
  },
});

export const firstVisibleIndex = (columns: IColumn[]): number => {
  return columns.findIndex(column => column.visible);
};

export const getPercentColumnIndex = (columns: IColumn[]): number => {
  return columns.findIndex(column => column.fieldType === 'ekreportprocent');
};

class EkonomiskAnalysRadBaseradTable extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      examineIndex: undefined,
      hoverRowIndex: -1,
    };
  }
  toggleExamine = (rowIndex: number) => {
    this.setState(prevState => {
      const sameIndex = rowIndex === prevState.examineIndex;
      return {
        examineIndex: sameIndex ? undefined : rowIndex,
      };
    });
  };

  showTableInfo = (
    e: React.MouseEvent<HTMLTableCellElement | HTMLTableHeaderCellElement>
  ) => {
    let currentElement: HTMLElement = e.currentTarget;
    let currentElementOffset = currentElement.offsetTop;
    let currentAncestor: HTMLTableElement | null =
      currentElement.closest('table');
    if (currentAncestor && currentElementOffset) {
      let currentAncestorHeight = currentAncestor.clientHeight;
      let currentAncestorOffset = currentAncestor.offsetTop;
      let currentPainter: HTMLElement | null =
        currentElement.querySelector('.colPaint');
      let yPos: string =
        '-' + (currentElementOffset - currentAncestorOffset) + 'px';
      if (currentPainter) {
        currentPainter.style.top = yPos;
        currentPainter.style.height = currentAncestorHeight + 'px';
      }
    }
  };

  startHoverRow = (rowIndex: number) => {
    this.setState({ hoverRowIndex: rowIndex });
  };

  stopHoverRow = () => {
    this.setState({ hoverRowIndex: -1 });
  };

  render() {
    const { classes } = this.props;
    const valueColumnIndexes = this.props.analys.columns.reduce<number[]>(
      (arr, column, index) => {
        if (column.fieldInfo.length > 0) {
          arr.push(index);
        }
        return arr;
      },
      []
    );
    return (
      <div
        className={classes.scrollContainer}
        onScroll={this.props.handleScroll}
        data-scroll=""
      >
        <EDTable stretch className={this.props.classes.table}>
          <EDTableHead>
            <EDTableRow>
              {this.props.analys.columns.map((col, colIndex) => {
                if (!col.visible) {
                  return null;
                }

                const isFirst =
                  colIndex === firstVisibleIndex(this.props.analys.columns);

                const isNot =
                  this.props.analys.columns[colIndex].fieldType === 'radnot';

                return (
                  <TableCell
                    key={colIndex}
                    variant="head"
                    alignment={isFirst ? 'left' : 'right'}
                    stickyTop={0} // fixme: was 72
                    paddingLeft={isFirst ? 0 : 24}
                    value={`${col.title1}${col.title2 ? '/' + col.title2 : ''}${
                      col.title3 ? '/' + col.title3 : ''
                    }`}
                    noVerticalSpacing
                    className={
                      isFirst
                        ? this.props.classes.firstColumnHeadCell
                        : isNot
                        ? this.props.classes.notCell
                        : this.props.classes.columnHead
                    }
                  >
                    {colIndex !== 0 && (
                      <EDTooltip title={col.inverted ? TECKENVAND_KOLUMN : ''}>
                        <EDTypography
                          variant={'body2'}
                          className={classes.columnTitle}
                          noWrap
                        >
                          {col.title1}
                          {col.title2 && <br />}
                          {col.title2}
                          {col.title3 && <br />}
                          {col.title3}
                        </EDTypography>
                      </EDTooltip>
                    )}
                    {isFirst && (
                      <EkonomiskAnalysDetailInfo
                        showComparsion={this.props.showComparisonNumber}
                        impersonatedCodePrivilege={
                          this.props.impersonatedCodePrivilege
                        }
                        analysSettings={this.props.analysSettings}
                        denomination={
                          this.props.updatedAnalysSettings.other.denomination
                        }
                        updatedAnalysSettingsUrval={
                          this.props.updatedAnalysSettings.urval
                        }
                        updateImpersonatedCodePrivilege={
                          this.props.updateImpersonatedCodePrivilege
                        }
                        onDenominationChange={this.props.onDenominationChange}
                        selectedAnalys={this.props.selectedAnalys}
                      />
                    )}
                  </TableCell>
                );
              })}
              <BaseTableCell
                className={this.props.classes.lastColumnHeadCell}
              />
            </EDTableRow>
          </EDTableHead>
          <EDTableBody>
            {this.props.analys.rows.map((row, rowIndex) => {
              if (row.filtered) {
                return null;
              }
              const isSumRow = row.rowType === 'S';

              if (isSumRow) {
                return (
                  <RowTypeS
                    key={`${rowIndex}`}
                    analys={this.props.analys}
                    analysSettings={this.props.analysSettings}
                    examineIndex={this.state.examineIndex}
                    impersonatedCodePrivilege={
                      this.props.impersonatedCodePrivilege
                    }
                    updatedAnalysSettings={this.props.updatedAnalysSettings}
                    updateImpersonatedCodePrivilege={
                      this.props.updateImpersonatedCodePrivilege
                    }
                    row={row}
                    rowIndex={rowIndex}
                    rows={this.props.analys.rows}
                    selectedAnalys={this.props.selectedAnalys}
                    toggleExamine={this.toggleExamine}
                    valueColumnIndexes={valueColumnIndexes}
                  />
                );
              }

              return (
                <DefaultRowType
                  key={`${rowIndex}`}
                  analys={this.props.analys}
                  analysSettings={this.props.analysSettings}
                  examineIndex={this.state.examineIndex}
                  impersonatedCodePrivilege={
                    this.props.impersonatedCodePrivilege
                  }
                  updatedAnalysSettings={this.props.updatedAnalysSettings}
                  updateImpersonatedCodePrivilege={
                    this.props.updateImpersonatedCodePrivilege
                  }
                  row={row}
                  rowIndex={rowIndex}
                  selectedAnalys={this.props.selectedAnalys}
                  showTableInfo={this.showTableInfo}
                  toggleExamine={this.toggleExamine}
                  valueColumnIndexes={valueColumnIndexes}
                />
              );
            })}
          </EDTableBody>
        </EDTable>
      </div>
    );
  }
}

export default withStyles(EkonomiskAnalysRadBaseradTable, styles);
